import React from 'react';
import { Layers, RotateCw } from 'lucide-react';
import { OrientationPreview } from './OrientationPreview';

/**
 * Orientation Controls - Manual/Auto orientation selection and rotation controls
 */
export const OrientationControls = ({
  optimizeOrientation,
  setOptimizeOrientation,
  manualRotation,
  setManualRotation,
  previewFile,
  file
}) => {
  // Rotation presets
  const rotationPresets = [
    { label: 'Original', x: 0, y: 0, z: 0 },
    { label: 'Rotate X 90°', x: 90, y: 0, z: 0 },
    { label: 'Rotate Y 90°', x: 0, y: 90, z: 0 },
    { label: 'Flip (180°)', x: 180, y: 0, z: 0 },
    { label: 'Rotate X -90°', x: -90, y: 0, z: 0 },
  ];

  return (
    <div className="bg-bg-light p-4 rounded-lg mb-4" data-testid="orientation-controls">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-5 h-5 text-accent" />
        Model Orientation
      </div>
      
      {/* Orientation Mode Selection */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setOptimizeOrientation(true)}
          className={`flex-1 px-4 py-2 rounded-lg transition-all ${
            optimizeOrientation
              ? 'bg-accent text-black font-semibold'
              : 'bg-bg-dark text-text-secondary hover:text-white'
          }`}
          data-testid="orientation-auto-btn"
        >
          <RotateCw className="w-4 h-4 inline mr-2" />
          Auto-Optimize
        </button>
        <button
          onClick={() => setOptimizeOrientation(false)}
          className={`flex-1 px-4 py-2 rounded-lg transition-all ${
            !optimizeOrientation
              ? 'bg-accent text-black font-semibold'
              : 'bg-bg-dark text-text-secondary hover:text-white'
          }`}
          data-testid="orientation-manual-btn"
        >
          Manual
        </button>
      </div>
      
      {/* Manual Rotation Controls */}
      {!optimizeOrientation && (
        <div className="space-y-4">
          {/* 3D Preview */}
          {file && previewFile && (
            <div className="mb-4">
              <p className="text-xs text-text-secondary mb-2 flex items-center gap-1">
                <span className="text-accent">●</span> Live Preview
              </p>
              <OrientationPreview 
                fileUrl={previewFile}
                rotation={manualRotation}
                height={280}
              />
            </div>
          )}
          
          {/* Quick Presets */}
          <div>
            <p className="text-sm text-text-secondary mb-2">Quick Presets:</p>
            <div className="flex flex-wrap gap-2">
              {rotationPresets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => setManualRotation({ x: preset.x, y: preset.y, z: preset.z })}
                  className={`px-3 py-1.5 text-xs rounded transition-all ${
                    manualRotation.x === preset.x && 
                    manualRotation.y === preset.y && 
                    manualRotation.z === preset.z
                      ? 'bg-accent text-black font-semibold'
                      : 'bg-bg-dark text-text-secondary hover:text-white hover:bg-bg-dark/80'
                  }`}
                  data-testid={`preset-${preset.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Rotation Sliders */}
          <div className="space-y-3">
            {/* X Rotation */}
            <div>
              <label className="block text-sm mb-1">
                <span className={manualRotation.x !== 0 ? 'text-accent font-semibold' : 'text-text-secondary'}>
                  X Rotation: {manualRotation.x}°
                </span>
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                step="15"
                value={manualRotation.x}
                onChange={(e) => setManualRotation(prev => ({ ...prev, x: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-red-500"
                data-testid="rotation-x-slider"
              />
              <div className="flex justify-between text-xs text-text-secondary">
                <span>-180°</span>
                <span>0°</span>
                <span>180°</span>
              </div>
            </div>
            
            {/* Y Rotation */}
            <div>
              <label className="block text-sm mb-1">
                <span className={manualRotation.y !== 0 ? 'text-accent font-semibold' : 'text-text-secondary'}>
                  Y Rotation: {manualRotation.y}°
                </span>
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                step="15"
                value={manualRotation.y}
                onChange={(e) => setManualRotation(prev => ({ ...prev, y: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-green-500"
                data-testid="rotation-y-slider"
              />
              <div className="flex justify-between text-xs text-text-secondary">
                <span>-180°</span>
                <span>0°</span>
                <span>180°</span>
              </div>
            </div>
            
            {/* Z Rotation */}
            <div>
              <label className="block text-sm mb-1">
                <span className={manualRotation.z !== 0 ? 'text-accent font-semibold' : 'text-text-secondary'}>
                  Z Rotation: {manualRotation.z}°
                </span>
              </label>
              <input
                type="range"
                min="-180"
                max="180"
                step="15"
                value={manualRotation.z}
                onChange={(e) => setManualRotation(prev => ({ ...prev, z: parseInt(e.target.value) }))}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
                data-testid="rotation-z-slider"
              />
              <div className="flex justify-between text-xs text-text-secondary">
                <span>-180°</span>
                <span>0°</span>
                <span>180°</span>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-text-secondary mt-2">
            💡 Tip: Position your model to minimize overhangs for better print quality
          </p>
        </div>
      )}
    </div>
  );
};

export default OrientationControls;
